import classNames from "classnames";
import React, { ChangeEvent } from "react";
import { ToggleColor } from "./toggleColor";

interface ToggleProps {
  checked?: boolean;
  color?: ToggleColor;
  disabled?: boolean;
  label?: string;
  name?: string;
  onChange?: (checked: boolean) => void;
}

export const Toggle = ({
  checked = false,
  color = ToggleColor.Neutral,
  disabled = false,
  label,
  name,
  onChange
}: ToggleProps) => {
  let colorClass: string;

  if (disabled) {
    colorClass = "bg-neutral-100";
  } else {
    switch (color) {
      case ToggleColor.OnDark: {
        colorClass = classNames(
          "bg-white/10 group-hover/toggle:bg-white/20",
          "peer-checked:bg-turquoise-800 peer-checked:group-hover/toggle:bg-turquoise-400",
          "peer-focus-visible:outline-light-blue-800/75"
        );
        break;
      }
      case ToggleColor.OnLight: {
        colorClass = classNames(
          "bg-black/5 group-hover/toggle:bg-black/10",
          "peer-checked:bg-dark-blue peer-checked:group-hover/toggle:bg-black/20",
          "peer-focus-visible:outline-dark-blue/75"
        );
        break;
      }
      case ToggleColor.Pink: {
        colorClass = classNames(
          "bg-neutral-200 group-hover/toggle:bg-neutral-300",
          "peer-checked:bg-pink-1000 peer-checked:group-hover/toggle:bg-pink-900",
          "peer-focus-visible:outline-dark-blue/75"
        );
        break;
      }
      case ToggleColor.Yellow: {
        colorClass = classNames(
          "bg-neutral-200 group-hover/toggle:bg-neutral-300",
          "peer-checked:bg-yellow-800 peer-checked:group-hover/toggle:bg-yellow-600",
          "peer-focus-visible:outline-dark-blue/75"
        );
        break;
      }
      case ToggleColor.Neutral:
      default: {
        colorClass = classNames(
          "bg-neutral-200 group-hover/toggle:bg-neutral-300",
          "peer-checked:bg-dark-blue peer-checked:group-hover/toggle:bg-turquoise-800",
          "peer-focus-visible:outline-dark-blue/75"
        );
        break;
      }
    }
  }

  const checkboxProperties = {
    name: name,
    type: "checkbox",
    className: "absolute -left-full peer",
    disabled: disabled,
    checked: checked,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e.target.checked);
      }
    }
  };

  return (
    <div>
      {/* Need to add padding together with a negative margin here, to be able to use overflow hidden
       while still showing the focus ring.  */}
      <label className="relative overflow-hidden group/toggle flex p-xs -m-xs items-center gap-sm cursor-pointer">
        <span>
          <input {...checkboxProperties} />

          <span
            className={classNames(
              "flex gap-xxs items-center rounded-md px-xxs transition-short-in-out",
              colorClass,
              "focus-outline peer-focus-visible:outline",
              "[.peer:not(:checked)_~_&_.checkbox-circle]:-translate-x-[27px]"
            )}
          >
            <svg
              className={
                "flex-none inline-flex justify-center items-center text-neutral-0 w-lg h-lg"
              }
              aria-hidden="true"
              focusable="false"
            >
              <use href="/svg/system/check.svg#icon" />
            </svg>

            <span
              className={classNames(
                "checkbox-circle",
                "transform duration-inherit ease-inherit flex-none",
                "rounded-full bg-neutral-0 w-[18px] h-[18px]"
              )}
            ></span>
          </span>
        </span>

        <span
          className={classNames("text-t-sm", {
            "text-dark-blue": !disabled,
            "text-neutral-200": disabled
          })}
        >
          {label}
        </span>
      </label>
    </div>
  );
};

Toggle.displayName = "Toggle";

export default Toggle;
